const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const setupMapLayersAside = block => {
    const toggle = block.querySelector('[data-map-layers-mobile-toggle]');
    const panel = block.querySelector('[data-map-layers-panel]');
    const chevron = block.querySelector('[data-map-layers-chevron]');

    if (!toggle || !panel) {
        return;
    }

    const applyExpandedState = expanded => {
        toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        panel.classList.toggle('hidden', !expanded);
        if (chevron) {
            chevron.classList.toggle('rotate-180', expanded);
        }
    };

    toggle.addEventListener('click', () => {
        const expanded = toggle.getAttribute('aria-expanded') === 'true';
        applyExpandedState(!expanded);
    });

    applyExpandedState(false);
};

const touchDistance = touchList => {
    if (touchList.length < 2) {
        return 0;
    }
    const a = touchList[0];
    const b = touchList[1];
    const dx = a.clientX - b.clientX;
    const dy = a.clientY - b.clientY;
    return Math.hypot(dx, dy);
};

const setupLayerVisibility = block => {
    const controls = block.querySelectorAll('input[data-layer-control]');
    controls.forEach(control => {
        const layerKey = control.dataset.layerControl;
        const overlay = block.querySelector(`[data-map-layer="${layerKey}"]`);

        const syncState = () => {
            const isChecked = control.checked;

            if (overlay) {
                overlay.classList.toggle('opacity-100', isChecked);
                overlay.classList.toggle('opacity-0', !isChecked);
                overlay.setAttribute(
                    'aria-hidden',
                    isChecked ? 'false' : 'true'
                );
                overlay.dataset.visible = isChecked ? 'true' : 'false';
            }
        };

        control.addEventListener('change', syncState);
        syncState();
    });
};

const setupZoomControls = block => {
    const stage = block.querySelector('[data-map-stage]');
    const wrapper = block.querySelector('[data-map-wrapper]');
    const zoomInBtn = block.querySelector('[data-zoom-in]');
    const zoomOutBtn = block.querySelector('[data-zoom-out]');

    if (!stage || !wrapper || !zoomInBtn || !zoomOutBtn) {
        return;
    }

    if (!wrapper.contains(stage)) {
        return;
    }

    /** Obsługa gestów na osobnej warstwie bez transformu — naprawia hit-testing po pan na iOS Safari. */
    const interaction = wrapper.querySelector('[data-map-interaction]');
    const hitLayer = interaction || stage;

    const scaleLayer = stage.querySelector('[data-map-scale]');

    let svgZoomNodes = [];

    const refreshSvgZoomNodes = () => {
        svgZoomNodes = scaleLayer
            ? [...scaleLayer.querySelectorAll('.map-layers__svg-root > svg')]
            : [];
    };

    /** Przy samej grafice wektorowej skalujemy bezpośrednio element svg — często ostro; przy img zostaje scale na kontenerze. */
    const usePerSvgScale = () =>
        Boolean(
            scaleLayer &&
                svgZoomNodes.length > 0 &&
                scaleLayer.querySelector('img') === null
        );

    const clearScaleTransitionStyles = () => {
        stage.style.transition = '';
        if (scaleLayer) {
            scaleLayer.style.transition = '';
        }
        refreshSvgZoomNodes();
        svgZoomNodes.forEach(svg => {
            svg.style.transition = '';
        });
    };

    const parseValue = (key, fallback) => {
        const raw = stage.dataset[key];
        const parsed = raw ? parseFloat(raw) : NaN;
        return Number.isFinite(parsed) ? parsed : fallback;
    };

    const minScale = parseValue('zoomMin', 0.5);
    const maxScale = parseValue('zoomMax', 5);
    const step = parseValue('zoomStep', 0.25);
    let scale = clamp(parseValue('zoomDefault', 1), minScale, maxScale);
    let translateX = 0;
    let translateY = 0;
    let isDragging = false;
    let pointerId = null;
    let dragStart = { x: 0, y: 0 };
    let pinchStartDistance = 0;
    let pinchStartScale = minScale;
    let gestureBaseScale = minScale;

    const updateButtons = () => {
        zoomOutBtn.disabled = scale <= minScale + 0.001;
        zoomInBtn.disabled = scale >= maxScale - 0.001;
    };

    const getPanExtents = () => {
        const extraWidth = stage.offsetWidth * scale - wrapper.clientWidth;
        const extraHeight = stage.offsetHeight * scale - wrapper.clientHeight;
        return {
            limitX: extraWidth > 0 ? extraWidth / 2 : 0,
            limitY: extraHeight > 0 ? extraHeight / 2 : 0,
        };
    };

    const canPan = () => {
        if (scale <= minScale + 0.001) {
            return false;
        }
        const { limitX, limitY } = getPanExtents();
        return limitX > 0.5 || limitY > 0.5;
    };

    const updateCursor = () => {
        if (canPan()) {
            hitLayer.classList.add('cursor-grab');
        } else {
            hitLayer.classList.remove('cursor-grab');
            hitLayer.classList.remove('cursor-grabbing');
        }
    };

    /**
     * Blokuje natywny scroll strony w pionie podczas przeciągania mapy (mobile),
     * gdy faktycznie można panować; przy zoomie 1× przywraca zwykłe scrollowanie.
     * touch-action na warstwie trafień (nie na przesuniętym stage) — spójność z iOS.
     */
    const syncGestureScrollLock = () => {
        if (canPan()) {
            hitLayer.style.pointerEvents = 'auto';
            hitLayer.style.touchAction = 'none';
            stage.style.touchAction = '';
            wrapper.style.overscrollBehavior = 'contain';
        } else {
            hitLayer.style.pointerEvents = 'none';
            hitLayer.style.touchAction = '';
            stage.style.touchAction = '';
            wrapper.style.overscrollBehavior = '';
        }
    };

    const clampTranslation = () => {
        if (scale <= minScale + 0.001) {
            translateX = 0;
            translateY = 0;
            return;
        }

        const { limitX, limitY } = getPanExtents();

        translateX = clamp(translateX, -limitX, limitX);
        translateY = clamp(translateY, -limitY, limitY);
    };

    const applyTransform = ({ immediate } = {}) => {
        clampTranslation();

        refreshSvgZoomNodes();
        const perSvg = usePerSvgScale();

        if (immediate) {
            stage.style.transition = 'none';
            if (perSvg) {
                if (scaleLayer) {
                    scaleLayer.style.transition = 'none';
                }
                svgZoomNodes.forEach(svg => {
                    svg.style.transition = 'none';
                });
            } else if (scaleLayer) {
                scaleLayer.style.transition = 'none';
            }
        }

        stage.style.transform = `translate(${translateX}px, ${translateY}px)`;

        if (scaleLayer) {
            if (perSvg) {
                scaleLayer.style.transform = '';
                svgZoomNodes.forEach(svg => {
                    svg.style.transformOrigin = '50% 50%';
                    svg.style.transform = `scale(${scale})`;
                });
            } else {
                svgZoomNodes.forEach(svg => {
                    svg.style.transform = '';
                    svg.style.transformOrigin = '';
                });
                scaleLayer.style.transform = `scale(${scale})`;
            }
        } else {
            stage.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scale})`;
        }

        if (immediate) {
            stage.getBoundingClientRect();
            clearScaleTransitionStyles();
        }

        updateButtons();
        updateCursor();
        syncGestureScrollLock();
    };

    const changeScale = delta => {
        const nextScale = clamp(
            parseFloat((scale + delta).toFixed(3)),
            minScale,
            maxScale
        );
        if (nextScale === scale) {
            return;
        }

        scale = nextScale;
        if (scale <= minScale + 0.001) {
            translateX = 0;
            translateY = 0;
        }

        refreshSvgZoomNodes();
        const perSvg = usePerSvgScale();
        if (perSvg) {
            svgZoomNodes.forEach(svg => {
                svg.style.transition = 'transform 0.25s ease-out';
            });
        } else if (scaleLayer) {
            scaleLayer.style.transition = 'transform 0.25s ease-out';
        } else {
            stage.style.transition = 'transform 0.25s ease-out';
        }
        applyTransform();
        window.setTimeout(() => {
            clearScaleTransitionStyles();
        }, 260);
    };

    zoomInBtn.addEventListener('click', () => changeScale(step));
    zoomOutBtn.addEventListener('click', () => changeScale(-step));

    /** Trackpad pinch w Chrome / Edge / Firefox: wheel + ctrlKey (czasem metaKey). */
    const wheelZoomSensitivity = 0.008;

    hitLayer.addEventListener(
        'wheel',
        event => {
            if (!event.ctrlKey && !event.metaKey) {
                return;
            }
            event.preventDefault();
            const next = clamp(
                scale - event.deltaY * wheelZoomSensitivity,
                minScale,
                maxScale
            );
            if (next === scale) {
                return;
            }
            scale = next;
            if (scale <= minScale + 0.001) {
                translateX = 0;
                translateY = 0;
            }
            applyTransform({ immediate: true });
        },
        { passive: false }
    );

    const cancelPointerDrag = () => {
        if (!isDragging) {
            return;
        }
        hitLayer.classList.remove('cursor-grabbing');
        if (pointerId !== null) {
            try {
                hitLayer.releasePointerCapture(pointerId);
            } catch {
                /* ignore */
            }
        }
        pointerId = null;
        isDragging = false;
        clearScaleTransitionStyles();
    };

    /** Pinch dwoma palcami (mobile / ekran dotykowy). */
    hitLayer.addEventListener(
        'touchstart',
        event => {
            if (event.touches.length === 2) {
                cancelPointerDrag();
                pinchStartDistance = touchDistance(event.touches);
                pinchStartScale = scale;
            }
        },
        { passive: true }
    );

    hitLayer.addEventListener(
        'touchmove',
        event => {
            if (event.touches.length !== 2 || pinchStartDistance <= 0) {
                return;
            }
            event.preventDefault();
            const d = touchDistance(event.touches);
            if (d <= 0) {
                return;
            }
            const next = clamp(
                pinchStartScale * (d / pinchStartDistance),
                minScale,
                maxScale
            );
            if (Math.abs(next - scale) < 0.0001) {
                return;
            }
            scale = next;
            if (scale <= minScale + 0.001) {
                translateX = 0;
                translateY = 0;
            }
            applyTransform({ immediate: true });
        },
        { passive: false }
    );

    const endPinchTouch = event => {
        if (event.touches.length < 2) {
            pinchStartDistance = 0;
        }
    };
    hitLayer.addEventListener('touchend', endPinchTouch, { passive: true });
    hitLayer.addEventListener('touchcancel', () => {
        pinchStartDistance = 0;
    });

    /** Safari (macOS / iOS): gesture pinch zamiast wheel+ctrl. */
    if (typeof window !== 'undefined' && 'GestureEvent' in window) {
        hitLayer.addEventListener(
            'gesturestart',
            event => {
                event.preventDefault();
                gestureBaseScale = scale;
            },
            { passive: false }
        );
        hitLayer.addEventListener(
            'gesturechange',
            event => {
                event.preventDefault();
                const next = clamp(
                    gestureBaseScale * event.scale,
                    minScale,
                    maxScale
                );
                scale = next;
                if (scale <= minScale + 0.001) {
                    translateX = 0;
                    translateY = 0;
                }
                applyTransform({ immediate: true });
            },
            { passive: false }
        );
        hitLayer.addEventListener(
            'gestureend',
            event => {
                event.preventDefault();
            },
            { passive: false }
        );
    }

    hitLayer.addEventListener('pointerdown', event => {
        if (!event.isPrimary) {
            return;
        }
        if (!canPan()) {
            return;
        }

        event.preventDefault();
        isDragging = true;
        pointerId = event.pointerId;
        dragStart = { x: event.clientX, y: event.clientY };
        hitLayer.setPointerCapture(pointerId);
        refreshSvgZoomNodes();
        const perSvg = usePerSvgScale();
        stage.style.transition = 'none';
        if (perSvg) {
            svgZoomNodes.forEach(svg => {
                svg.style.transition = 'none';
            });
        } else if (scaleLayer) {
            scaleLayer.style.transition = 'none';
        }
        hitLayer.classList.add('cursor-grabbing');
    });

    hitLayer.addEventListener('pointermove', event => {
        if (!isDragging || pointerId !== event.pointerId) {
            return;
        }

        const deltaX = event.clientX - dragStart.x;
        const deltaY = event.clientY - dragStart.y;
        dragStart = { x: event.clientX, y: event.clientY };

        translateX += deltaX;
        translateY += deltaY;

        applyTransform({ immediate: true });
    });

    const endDrag = event => {
        if (
            !isDragging ||
            (pointerId !== null && event.pointerId !== pointerId)
        ) {
            return;
        }

        hitLayer.classList.remove('cursor-grabbing');

        if (pointerId !== null) {
            hitLayer.releasePointerCapture(pointerId);
        }

        pointerId = null;
        isDragging = false;
        clearScaleTransitionStyles();
        applyTransform();
    };

    hitLayer.addEventListener('pointerup', endDrag);
    hitLayer.addEventListener('pointercancel', endDrag);
    hitLayer.addEventListener('pointerleave', event => {
        if (!isDragging) {
            return;
        }
        /** Na touchu pointerleave bywa fałszywe przy ruchu palcem — nie przerywaj panowania. */
        if (event.pointerType === 'touch') {
            return;
        }
        endDrag(event);
    });

    stage.querySelectorAll('img').forEach(img => {
        if (img.complete) {
            return;
        }
        img.addEventListener('load', () =>
            window.requestAnimationFrame(applyTransform)
        );
    });

    if (typeof ResizeObserver !== 'undefined') {
        const resizeObserver = new ResizeObserver(() => {
            window.requestAnimationFrame(applyTransform);
        });
        resizeObserver.observe(wrapper);
    }

    applyTransform();
};

const initLayerBlock = block => {
    if (block.dataset.mapLayersInit === 'true') {
        return;
    }

    block.dataset.mapLayersInit = 'true';

    setupMapLayersAside(block);
    setupLayerVisibility(block);
    setupZoomControls(block);
};

const initMapLayers = () => {
    const blocks = document.querySelectorAll('.js-map-layers');
    if (!blocks.length) {
        return;
    }

    blocks.forEach(initLayerBlock);
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMapLayers);
} else {
    initMapLayers();
}

if (typeof window !== 'undefined' && typeof window.acf !== 'undefined') {
    window.acf.addAction('render_block_preview/type=map-layers', initMapLayers);
}
